/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Button, SecurityHelper, Select } from 'sarsaparilla';
import PropTypes from 'prop-types';
import values from 'lodash/values';
import { useNavigate } from 'react-router';
import { gnoment } from 'shared-ui/gnoment';
import { authModalActions } from 'shared-ui/auth/redux';
import { PERMIT, SEASON } from '../propTypes';
import { ALL_DIVISION_TO_TYPE_MAPPING } from '../utils/utils';

export default function BigCypressSidebar({ permit, seasons, pActions }) {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [selectedPermitType, setSelectedPermitType] = useState(null);
    const [waitingForUserAccount, setWaitingForUserAccount] = useState(false);
    const isUserLoggedIn = SecurityHelper.isLoggedIn();
    const account = SecurityHelper.getAccount();

    useEffect(() => {
        pActions.purgePendingReservations();
    }, []);

    const continueClick = useCallback(() => {
        if (!isUserLoggedIn) {
            setWaitingForUserAccount(true);
            dispatch(authModalActions.openLoginModal());
            return;
        }

        const divisionKey = Object.keys(permit.divisions)[0];
        const division = permit.divisions?.[divisionKey];

        if (!division?.id) return;

        const item = {
            division_id: selectedPermitType,
            permit_type: ALL_DIVISION_TO_TYPE_MAPPING[selectedPermitType],
            permit_id: permit.id,
            start_date: gnoment().format('YYYY-MM-DD'),
            group_members: [
                {
                    birth_date: '',
                    first_name: account.first_name,
                    middle_name: '',
                    last_name: account.last_name,
                },
            ],
        };
        pActions.createPendingReservations([item]);
        navigate(`/vehiclepermits/${permit.id}/order/detail`);
    }, [
        account.first_name,
        account.last_name,
        dispatch,
        navigate,
        isUserLoggedIn,
        pActions,
        permit.divisions,
        permit.id,
        selectedPermitType,
    ]);

    useEffect(() => {
        if (isUserLoggedIn && waitingForUserAccount && account) {
            continueClick();
            setWaitingForUserAccount(false);
        }
    }, [waitingForUserAccount, isUserLoggedIn, account, continueClick]);

    const dropdownOptions = values(permit.divisions).map((div) => ({
        label: div.name,
        value: div.id,
    }));

    const couldContinue = !!selectedPermitType;

    const changePermitTypeSelection = useCallback((event) => {
        setSelectedPermitType(event.target.value);
    }, []);

    return (
        <>
            <p>Before you get started, please select the length of your stay.</p>
            <Select
                className="mb-4"
                label="Permit Type"
                id="dropdown-permit-type"
                placeholder="Select a permit type"
                options={dropdownOptions}
                isDisabled={!seasons?.length}
                onChange={changePermitTypeSelection}
                value={selectedPermitType}
            />
            <Button
                onClick={continueClick}
                className="rec-button-primary-large bottom-3"
                shouldFitContainer
                isDisabled={!couldContinue || !seasons?.length}
            >
                Get Permits
            </Button>
        </>
    );
}

BigCypressSidebar.propTypes = {
    permit: PERMIT.isRequired,
    seasons: PropTypes.arrayOf(SEASON),
    pActions: PropTypes.shape().isRequired,
};
